import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import type { IconWeight } from "@phosphor-icons/react";
import {
  Folder,
  GraduationCap,
  ChatCircle,
  Plus,
  Sun,
  Moon,
  Desktop,
  CaretDown,
  MagnifyingGlass,
  ArrowLineLeft,
  Gear,
  SignOut,
  User as UserIcon,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { APP_NAME, LeverMark, appNameStyle } from "../../lib/brand";
import { useUIStore, type Theme } from "../../stores/ui";
import { useDocumentStore } from "../../stores/document";
import { useHomeworkStore } from "../../stores/homework";
import { useAuthStore } from "../../stores/auth";

const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 52;
const MOBILE_BREAKPOINT = 768;

const spring = { type: "spring" as const, stiffness: 400, damping: 32, mass: 0.8 };
const springFast = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

type NavId = "projects" | "classes";

const NAV_ITEMS: { id: NavId; label: string; path: string; icon: typeof Folder }[] = [
  { id: "projects", label: "Projects", path: "/", icon: Folder },
  { id: "classes", label: "Classes", path: "/classes", icon: GraduationCap },
];

const THEME_OPTIONS: { id: Theme; label: string; icon: typeof Sun }[] = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
  { id: "system", label: "System", icon: Desktop },
];

function SidebarSection({
  title,
  icon: _Icon,
  defaultOpen = true,
  onAdd,
  collapsed,
  children,
}: {
  title: string;
  icon: React.ElementType;
  defaultOpen?: boolean;
  onAdd?: () => void;
  collapsed: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) return null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-0.5 px-2 py-0.5">
        <button
          onClick={() => setOpen(!open)}
          className={cn(
            "flex flex-1 items-center gap-1.5 rounded-[var(--radius-sm)] py-1 px-1.5 text-left",
            "text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]",
            "hover:text-[var(--text-secondary)]",
            "transition-colors duration-100",
          )}
        >
          <motion.span
            animate={{ rotate: open ? 0 : -90 }}
            transition={springFast}
            className="inline-flex"
          >
            <CaretDown size={9} weight="bold" />
          </motion.span>
          {title}
        </button>
        {onAdd && (
          <motion.button
            onClick={onAdd}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className={cn(
              "flex h-4 w-4 items-center justify-center rounded-[3px]",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-100",
            )}
            aria-label={`Add ${title.toLowerCase()}`}
          >
            <Plus size={10} weight="bold" />
          </motion.button>
        )}
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ ...spring, opacity: { duration: 0.15 } }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-px px-1.5 pb-1.5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SidebarItem({
  label,
  sublabel,
  icon: Icon,
  active,
  onClick,
}: {
  label: string;
  sublabel?: string;
  icon: React.ComponentType<{ size?: number; weight?: IconWeight }>;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <motion.button
      onClick={onClick}
      layout
      transition={springFast}
      className={cn(
        "relative group flex items-center gap-2 w-full rounded-[var(--radius-sm)] px-2 py-1 text-left",
        "transition-colors duration-75",
        active
          ? "text-[var(--text-primary)]"
          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
      )}
    >
      {active && (
        <motion.div
          layoutId="sidebar-item-active"
          className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface-hover)]"
          transition={spring}
        />
      )}
      <div className={cn("relative z-[1] flex h-5 w-5 shrink-0 items-center justify-center", active ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]")}>
        <Icon size={13} weight={active ? "fill" : "regular"} />
      </div>
      <div className="relative z-[1] flex-1 min-w-0">
        <span className="text-[12px] font-medium leading-tight truncate block">
          {label}
        </span>
        {sublabel && (
          <span className="text-[10px] text-[var(--text-muted)] truncate block leading-tight">
            {sublabel}
          </span>
        )}
      </div>
    </motion.button>
  );
}

function ThemeSelector({ theme, onSelect }: { theme: Theme; onSelect: (t: Theme) => void }) {
  return (
    <div className="flex items-center gap-0.5 rounded-[var(--radius-md)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
      {THEME_OPTIONS.map((opt) => {
        const Icon = opt.icon;
        const active = theme === opt.id;
        return (
          <button
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className={cn(
              "relative flex flex-1 items-center justify-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[10px] font-medium",
              "transition-colors duration-100",
              active ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
            )}
          >
            {active && (
              <motion.div
                layoutId="theme-selector-pill"
                className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
                transition={springFast}
              />
            )}
            <span className="relative z-[1]"><Icon size={11} weight={active ? "fill" : "regular"} /></span>
            <span className="relative z-[1]">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function UserMenu({ collapsed: _collapsed }: { collapsed: boolean }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ left: 0, bottom: 0 });
  const navigate = useNavigate();
  const { user, devMode, signOut } = useAuthStore();
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);

  const displayName = user?.user_metadata?.name || user?.email?.split("@")[0] || (devMode ? "Developer" : "User");
  const displayEmail = user?.email || (devMode ? "dev@lever.local" : "");
  const initial = displayName.charAt(0).toUpperCase();

  const handleToggle = useCallback(() => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({ left: rect.left, bottom: window.innerHeight - rect.top + 6 });
    }
    setOpen((v) => !v);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <div className="relative">
      <motion.button
        ref={triggerRef}
        onClick={handleToggle}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full",
          "bg-[var(--text-primary)] text-[9px] font-bold text-[var(--text-inverse)]",
          "cursor-pointer",
          open && "ring-2 ring-[var(--color-lever-orange)]/40",
        )}
        aria-label="User menu"
      >
        {initial}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.92, y: 4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 4 }}
            transition={springFast}
            className={cn(
              "fixed z-[100]",
              "w-56 rounded-[var(--radius-xl)] p-1.5",
              "bg-[var(--bg-surface)] border border-[var(--border-subtle)]",
              "shadow-[var(--shadow-xl)]",
            )}
            style={{ left: menuPos.left, bottom: menuPos.bottom }}
          >
            {/* User info */}
            <div className="px-2.5 py-2 border-b border-[var(--border-hairline)] mb-1">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--text-primary)] text-[11px] font-bold text-[var(--text-inverse)]">
                  {initial}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{displayName}</div>
                  {displayEmail && (
                    <div className="text-[10px] text-[var(--text-muted)] truncate">{displayEmail}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Theme selector */}
            <div className="px-2 py-1.5">
              <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-1.5 px-0.5">Theme</div>
              <ThemeSelector theme={theme} onSelect={(t) => setTheme(t)} />
            </div>

            <div className="mx-1.5 my-1 h-px bg-[var(--border-hairline)]" />

            {/* Menu items */}
            <button
              onClick={() => { navigate("/settings"); setOpen(false); }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-left",
                "text-[12px] text-[var(--text-secondary)]",
                "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
                "transition-colors duration-75",
              )}
            >
              <Gear size={14} />
              <span className="font-medium">Settings</span>
            </button>
            <button
              onClick={async () => { await signOut(); navigate("/landing"); setOpen(false); }}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-left",
                "text-[12px] text-[var(--color-soft-red)]",
                "hover:bg-[var(--color-soft-red)]/10",
                "transition-colors duration-75",
              )}
            >
              <SignOut size={14} />
              <span className="font-medium">Sign out</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
}

export function DashboardShell() {
  const navigate = useNavigate();
  const location = useLocation();

  const collapsed = useUIStore((s) => s.dashboardSidebarCollapsed);
  const setCollapsed = useUIStore((s) => s.setDashboardSidebarCollapsed);

  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  const { projects, fetchProjects } = useDocumentStore();
  const { courses, fetchCourses } = useHomeworkStore();

  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchProjects();
    fetchCourses();
  }, [fetchProjects, fetchCourses]);

  const activeNav: NavId = location.pathname.startsWith("/classes")
    ? "classes"
    : "projects";

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) return projects;
    const q = searchQuery.toLowerCase();
    return projects.filter(
      (p) => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q),
    );
  }, [projects, searchQuery]);

  const filteredCourses = useMemo(() => {
    if (!searchQuery.trim()) return courses;
    const q = searchQuery.toLowerCase();
    return courses.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.instructor?.toLowerCase().includes(q) ||
        c.term?.toLowerCase().includes(q),
    );
  }, [courses, searchQuery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;

      if (isMod && e.key === "k") {
        e.preventDefault();
        if (collapsed) setCollapsed(false);
        if (isMobile) setMobileOpen(true);
        requestAnimationFrame(() => searchInputRef.current?.focus());
      }
      if (isMod && e.key === "n") {
        e.preventDefault();
        navigate("/?create=true");
      }
      if (isMod && e.key === ",") {
        e.preventDefault();
        navigate("/settings");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [collapsed, setCollapsed, isMobile, navigate]);

  const handleLogoClick = useCallback(() => {
    if (collapsed) {
      setCollapsed(false);
    } else {
      navigate("/");
    }
  }, [collapsed, setCollapsed, navigate]);

  // Mobile: close sidebar on navigation
  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const sidebarContent = (
    <motion.aside
      layout={!isMobile}
      animate={isMobile ? undefined : { width: sidebarWidth }}
      transition={spring}
      className={cn(
        "flex h-full shrink-0 flex-col overflow-hidden",
        "border-r border-[var(--border-hairline)]",
        "bg-[var(--bg-surface)]",
        isMobile && "w-[260px]",
      )}
    >
      {/* Logo + collapse */}
      <div className="flex items-center gap-2 px-3 pt-3 pb-1.5 shrink-0">
        <motion.button
          onClick={handleLogoClick}
          className="flex items-center gap-2 min-w-0"
          layout={!isMobile}
          transition={spring}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center">
            <LeverMark size={22} className="text-[var(--text-primary)]" />
          </div>
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={{ opacity: 0, width: 0 }}
                transition={spring}
                className="text-[14px] font-semibold tracking-[0.01em] text-[var(--text-primary)] whitespace-nowrap overflow-hidden"
                style={appNameStyle}
              >
                {APP_NAME}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        {!collapsed && !isMobile && (
          <motion.button
            onClick={() => setCollapsed(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={cn(
              "ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-100",
            )}
            aria-label="Collapse sidebar"
          >
            <ArrowLineLeft size={13} />
          </motion.button>
        )}
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className={cn(
              "ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-100",
            )}
            aria-label="Close sidebar"
          >
            <ArrowLineLeft size={13} />
          </button>
        )}
      </div>

      {/* Search */}
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ ...spring, opacity: { duration: 0.12 } }}
            className="px-2 pb-1 overflow-hidden"
          >
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-[var(--radius-sm)]",
                "border border-[var(--border-hairline)] bg-[var(--bg-base)]",
                "px-2 py-1 text-[11px] text-[var(--text-muted)]",
                "focus-within:border-[var(--border-subtle)]",
                "transition-colors duration-100",
              )}
            >
              <MagnifyingGlass size={12} className="shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className={cn(
                  "flex-1 bg-transparent text-[11px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)]",
                  "outline-none min-w-0",
                )}
              />
              {searchQuery ? (
                <button
                  onClick={() => setSearchQuery("")}
                  className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <span className="text-[10px]">&#x2715;</span>
                </button>
              ) : (
                <kbd className="rounded border border-[var(--border-hairline)] bg-[var(--bg-surface)] px-1 text-[9px] font-medium text-[var(--text-muted)]">
                  ⌘K
                </kbd>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main nav tabs */}
      <div className={cn("flex shrink-0 gap-0.5 px-2 py-1", collapsed ? "flex-col" : "flex-row")}>
        {NAV_ITEMS.map((item) => {
          const isActive = activeNav === item.id;
          const Icon = item.icon;
          return (
            <motion.button
              key={item.id}
              onClick={() => navigate(item.path)}
              layout={!isMobile}
              transition={springFast}
              className={cn(
                "relative flex items-center gap-1.5 rounded-[var(--radius-sm)] text-left",
                collapsed ? "justify-center p-2" : "flex-1 px-2 py-1.5",
                "transition-colors duration-75",
                isActive ? "text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              )}
            >
              {isActive && (
                <motion.div
                  layoutId="nav-pill"
                  className="absolute inset-0 rounded-[var(--radius-sm)] bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]"
                  transition={spring}
                />
              )}
              <span className="relative z-[1]">
                <Icon size={14} weight={isActive ? "fill" : "regular"} />
              </span>
              {!collapsed && (
                <span className="relative z-[1] text-[12px] font-medium">
                  {item.label}
                </span>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Divider */}
      <div className="mx-2 border-t border-[var(--border-hairline)]" />

      {/* Scrollable sections */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1">
        {collapsed ? (
          <div className="flex flex-col items-center gap-1 py-1">
            {projects.slice(0, 4).map((p) => (
              <motion.button
                key={p.id}
                onClick={() => navigate(`/project/${p.id}`)}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)]",
                  "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-100",
                )}
                title={p.name}
              >
                <Folder size={14} weight="regular" />
              </motion.button>
            ))}
            {courses.slice(0, 4).map((c) => (
              <motion.button
                key={c.id}
                onClick={() => navigate(`/classes/${c.id}`)}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)]",
                  "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-100",
                )}
                title={c.name}
              >
                <GraduationCap size={14} weight="regular" />
              </motion.button>
            ))}
          </div>
        ) : (
          <>
            <SidebarSection
              title="Projects"
              icon={Folder}
              collapsed={collapsed}
              onAdd={() => navigate("/?create=true")}
            >
              {filteredProjects.length === 0 ? (
                <div className="px-2 py-2 text-[11px] text-[var(--text-muted)] text-center">
                  {searchQuery ? "No matches" : "No projects yet"}
                </div>
              ) : (
                filteredProjects.slice(0, 10).map((p) => (
                  <SidebarItem
                    key={p.id}
                    label={p.name}
                    sublabel={formatDate(p.updated_at || p.created_at)}
                    icon={Folder}
                    active={location.pathname === `/project/${p.id}`}
                    onClick={() => navigate(`/project/${p.id}`)}
                  />
                ))
              )}
              {filteredProjects.length > 10 && (
                <button
                  onClick={() => navigate("/")}
                  className="px-2 py-0.5 text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--color-lever-orange)] transition-colors"
                >
                  View all {filteredProjects.length}...
                </button>
              )}
            </SidebarSection>

            <SidebarSection
              title="Classes"
              icon={GraduationCap}
              collapsed={collapsed}
              onAdd={() => navigate("/classes?create=true")}
            >
              {filteredCourses.length === 0 ? (
                <div className="px-2 py-2 text-[11px] text-[var(--text-muted)] text-center">
                  {searchQuery ? "No matches" : "No classes yet"}
                </div>
              ) : (
                filteredCourses.slice(0, 10).map((c) => (
                  <SidebarItem
                    key={c.id}
                    label={c.name}
                    sublabel={[c.instructor, c.term].filter(Boolean).join(" · ") || undefined}
                    icon={GraduationCap}
                    active={location.pathname === `/classes/${c.id}`}
                    onClick={() => navigate(`/classes/${c.id}`)}
                  />
                ))
              )}
              {filteredCourses.length > 10 && (
                <button
                  onClick={() => navigate("/classes")}
                  className="px-2 py-0.5 text-[10px] font-medium text-[var(--text-muted)] hover:text-[var(--color-lever-orange)] transition-colors"
                >
                  View all {filteredCourses.length}...
                </button>
              )}
            </SidebarSection>
          </>
        )}
      </div>

      {/* Footer */}
      <div className={cn(
        "flex items-center border-t border-[var(--border-hairline)] shrink-0 gap-1.5",
        collapsed ? "flex-col justify-center px-1.5 py-2" : "justify-between px-2.5 py-2",
      )}>
        <UserMenu collapsed={collapsed} />
        <motion.button
          onClick={() => navigate("/chat")}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.92 }}
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded-full",
            "bg-[var(--bg-base)] border border-[var(--border-hairline)]",
            "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            "hover:border-[var(--border-subtle)] hover:shadow-[var(--shadow-sm)]",
            "transition-all duration-100",
          )}
          aria-label="Open chat"
          title="Chat"
        >
          <ChatCircle size={14} weight="regular" />
        </motion.button>
      </div>
    </motion.aside>
  );

  if (isMobile) {
    return (
      <div className="flex h-dvh w-dvw overflow-hidden bg-[var(--bg-primary)]">
        {/* Mobile hamburger */}
        <div className="absolute top-3 left-3 z-30">
          {!mobileOpen && (
            <motion.button
              onClick={() => setMobileOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.92 }}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)]",
                "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
                "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                "shadow-[var(--shadow-sm)]",
                "transition-colors duration-100",
              )}
              aria-label="Open sidebar"
            >
              <UserIcon size={16} />
            </motion.button>
          )}
        </div>

        {/* Mobile drawer overlay */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setMobileOpen(false)}
              />
              <motion.div
                className="fixed inset-y-0 left-0 z-50"
                initial={{ x: -280 }}
                animate={{ x: 0 }}
                exit={{ x: -280 }}
                transition={spring}
              >
                {sidebarContent}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh w-dvw overflow-hidden bg-[var(--bg-primary)]">
      <LayoutGroup>
        {sidebarContent}

        {/* Main content */}
        <motion.div layout transition={spring} className="flex flex-1 flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </motion.div>
      </LayoutGroup>
    </div>
  );
}
